// A small PDF writer: one rendered image per page, sized to the page (96 CSS px = 72 pt).
// Opaque pages are embedded as JPEG; transparent ones as deflated RGB with an alpha mask, so the
// background really is see-through when the PDF is placed in another document.
// Pages are rendered and encoded one at a time, and each canvas is freed before the next: iPad Safari
// has a small budget for canvases and quietly leaves new ones blank once it is used up.
import { releaseCanvas } from "./export";

export interface PdfSheet {
  /** the rendered page */
  canvas: HTMLCanvasElement;
  /** page size in CSS pixels (world units) */
  width: number;
  height: number;
  transparent: boolean;
}

const enc = new TextEncoder();

/** Collects bytes, deflating them on the fly when the browser can (raw bytes otherwise). */
function sink(): { write(b: Uint8Array): Promise<void>; finish(): Promise<{ bytes: Uint8Array; deflated: boolean }> } {
  if (typeof CompressionStream !== "undefined") {
    const cs = new CompressionStream("deflate");
    const out = new Response(cs.readable).arrayBuffer();
    const w = cs.writable.getWriter();
    return {
      write: (b) => w.write(b as BufferSource),
      finish: async () => {
        await w.close();
        return { bytes: new Uint8Array(await out), deflated: true };
      }
    };
  }
  const parts: Uint8Array[] = [];
  return {
    write: async (b) => void parts.push(b),
    finish: async () => {
      const bytes = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
      let o = 0;
      for (const p of parts) {
        bytes.set(p, o);
        o += p.length;
      }
      return { bytes, deflated: false };
    }
  };
}

/** The canvas as deflated RGB plus an alpha channel, read a strip at a time to keep memory low. */
async function splitChannels(canvas: HTMLCanvasElement) {
  const { width: w, height: h } = canvas;
  const ctx = canvas.getContext("2d")!;
  const rgb = sink();
  const alpha = sink();
  const rows = Math.max(1, Math.floor(4_000_000 / Math.max(1, w)));
  for (let y = 0; y < h; y += rows) {
    const n = Math.min(rows, h - y);
    const px = ctx.getImageData(0, y, w, n).data;
    const c = new Uint8Array(w * n * 3);
    const a = new Uint8Array(w * n);
    for (let i = 0, j = 0, k = 0; i < px.length; i += 4, j += 3, k++) {
      c[j] = px[i];
      c[j + 1] = px[i + 1];
      c[j + 2] = px[i + 2];
      a[k] = px[i + 3];
    }
    await Promise.all([rgb.write(c), alpha.write(a)]);
  }
  return Promise.all([rgb.finish(), alpha.finish()]);
}

function jpegBytes(canvas: HTMLCanvasElement): Promise<Uint8Array> {
  return new Promise((res, rej) =>
    canvas.toBlob((b) => (b ? b.arrayBuffer().then((a) => res(new Uint8Array(a)), rej) : rej(new Error("Couldn't make the PDF."))), "image/jpeg", 0.92)
  );
}

/** PDF text string: plain ASCII as a literal, anything else as UTF-16BE hex. */
function pdfString(s: string): string {
  if (/^[\x20-\x7e]*$/.test(s)) return "(" + s.replace(/[\\()]/g, (c) => "\\" + c) + ")";
  let hex = "FEFF";
  for (let i = 0; i < s.length; i++) hex += s.charCodeAt(i).toString(16).padStart(4, "0").toUpperCase();
  return "<" + hex + ">";
}

class Writer {
  private chunks: Uint8Array[] = [];
  private size = 0;
  private offsets: number[] = [];
  private next = 1;

  reserve(): number {
    return this.next++;
  }

  write(part: string | Uint8Array): void {
    const bytes = typeof part === "string" ? enc.encode(part) : part;
    this.chunks.push(bytes);
    this.size += bytes.length;
  }

  object(num: number, dict: string, stream?: Uint8Array): void {
    this.offsets[num] = this.size;
    this.write(`${num} 0 obj\n${dict}\n`);
    if (stream) {
      this.write("stream\n");
      this.write(stream);
      this.write("\nendstream\n");
    }
    this.write("endobj\n");
  }

  finish(root: number, info: number): Blob {
    const xref = this.size;
    let table = `xref\n0 ${this.next}\n0000000000 65535 f \n`;
    for (let i = 1; i < this.next; i++) table += String(this.offsets[i] ?? 0).padStart(10, "0") + " 00000 n \n";
    this.write(table);
    this.write(`trailer\n<< /Size ${this.next} /Root ${root} 0 R /Info ${info} 0 R >>\nstartxref\n${xref}\n%%EOF\n`);
    return new Blob(this.chunks as BlobPart[], { type: "application/pdf" });
  }
}

/** A PDF of `count` pages; `page(i)` draws page i. Pages are drawn, encoded and freed one at a time. */
export async function makePdf(count: number, page: (i: number) => Promise<PdfSheet>, title: string): Promise<Blob> {
  const w = new Writer();
  w.write("%PDF-1.4\n%âãÏÓ\n");
  const catalog = w.reserve();
  const pagesObj = w.reserve();
  const info = w.reserve();
  const kids: number[] = [];
  for (let n = 0; n < count; n++) {
    const sheet = await page(n);
    const pageObj = w.reserve();
    const content = w.reserve();
    const image = w.reserve();
    kids.push(pageObj);
    const pw = +(sheet.width * 0.75).toFixed(2);
    const ph = +(sheet.height * 0.75).toFixed(2);
    const { width: iw, height: ih } = sheet.canvas;
    try {
      if (sheet.transparent) {
        const [rgb, alpha] = await splitChannels(sheet.canvas);
        const mask = w.reserve();
        const filter = (z: { deflated: boolean }) => (z.deflated ? " /Filter /FlateDecode" : "");
        w.object(mask, `<< /Type /XObject /Subtype /Image /Width ${iw} /Height ${ih} /ColorSpace /DeviceGray /BitsPerComponent 8${filter(alpha)} /Length ${alpha.bytes.length} >>`, alpha.bytes);
        w.object(image, `<< /Type /XObject /Subtype /Image /Width ${iw} /Height ${ih} /ColorSpace /DeviceRGB /BitsPerComponent 8${filter(rgb)} /SMask ${mask} 0 R /Length ${rgb.bytes.length} >>`, rgb.bytes);
      } else {
        const jpeg = await jpegBytes(sheet.canvas);
        w.object(image, `<< /Type /XObject /Subtype /Image /Width ${iw} /Height ${ih} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpeg.length} >>`, jpeg);
      }
    } finally {
      releaseCanvas(sheet.canvas);
    }
    const draw = enc.encode(`q ${pw} 0 0 ${ph} 0 0 cm /Im0 Do Q`);
    w.object(content, `<< /Length ${draw.length} >>`, draw);
    w.object(pageObj, `<< /Type /Page /Parent ${pagesObj} 0 R /MediaBox [0 0 ${pw} ${ph}] /Resources << /XObject << /Im0 ${image} 0 R >> >> /Contents ${content} 0 R >>`);
  }
  w.object(pagesObj, `<< /Type /Pages /Kids [${kids.map((k) => `${k} 0 R`).join(" ")}] /Count ${kids.length} >>`);
  w.object(catalog, `<< /Type /Catalog /Pages ${pagesObj} 0 R >>`);
  w.object(info, `<< /Title ${pdfString(title)} /Producer (Sepia) >>`);
  return w.finish(catalog, info);
}
